import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import {
  LockboxCheck,
  LockboxCheckType,
} from '@/modules/employer/components/Lockbox/lockbox.types';

export type LockboxChecksQueryMode = 'SUGGESTED' | 'MANUAL';

interface Props {
  mode: LockboxChecksQueryMode;
  searchValue?: string;
  statuses?: string[];
  enabled?: boolean;
}

const useGetLockboxChecksQuery = ({ mode, searchValue, statuses, enabled }: Props) => {
  const params = {
    ...(searchValue ? { search_value: searchValue } : {}),
    ...(statuses?.length ? { check_status_types: statuses.join(',') } : {}),
  };

  const { data, isLoading } = useQuery(
    [API_URL.CHECKS_SEARCH, mode, searchValue, statuses],
    () => api.get(API_URL.CHECKS_SEARCH, { params }),
    {
      enabled: enabled !== false && (!!searchValue || !!statuses?.length),
    }
  );

  const checks = useMemo<LockboxCheck[]>(
    () =>
      (data?.data?.content || []).map((check): LockboxCheck => ({
        checkId: check?.check_id ? toString(check.check_id) : undefined,
        amount: typeof check?.amount === 'number' ? check.amount : undefined,
        employeeName: check?.employee_name,
        employeeId: check?.employee_id ? toString(check.employee_id) : undefined,
        claimIds: check?.claim_ids?.length ? check.claim_ids.map((id) => toString(id)) : undefined,
        issuedDate: check?.issued_date as unknown as string,
        statusDate: check?.status_date as unknown as string,
        status: check?.current_status,
        payTo: check?.pay_to,
        lockboxId: check?.lockbox_id ? toString(check.lockbox_id) : undefined,
        checkType: check?.check_type as unknown as LockboxCheckType,
        paymentMethodId: toString(check?.payment_method_id),
        address: check?.check_address
          ? {
              line1: check.check_address?.line1,
              line2: check.check_address?.line2,
              city: check.check_address?.city,
              state: {
                id: toString(check.check_address?.state?.id),
                code: check.check_address?.state?.code,
                name: check.check_address?.state?.name,
              },
              zipcode: check.check_address.zipcode,
            }
          : undefined,
      })),
    [data]
  );

  return {
    checks,
    isLoading,
  };
};

export default useGetLockboxChecksQuery;
